/**
 * HO-HU-36: los seis pasos de la guia de primeros pasos del manager.
 *
 * Aca viven los textos y los destinos; el estado (que esta hecho y que no) lo
 * resuelve el backend en /condominium/onboarding/{condominium}. Las claves
 * (`key`) tienen que coincidir con OnboardingController::STEPS.
 *
 * `to(condominiumId)` devuelve el destino tal cual lo espera el history de
 * react-router. Dos detalles que no son opcionales:
 *
 *   - `?onboarding=<key>` es lo que hace que el modulo de destino muestre el
 *     banner del paso y abra el modal correcto (ver OnboardingBanner). Va por
 *     query param y no por estado global para que sobreviva un refresh y para
 *     que no quede nada colgado si el usuario navega a otra parte.
 *   - el modulo de gastos ordinarios NECESITA `state.type_id`: sin el, la
 *     lista no filtra por tipo, y de hecho CommonCosts.jsx lee `state.type_name`
 *     sin proteger, asi que entrar a esa URL sin el state rompe la pantalla.
 *     Es el mismo state que ya manda Config.jsx en su link a ese modulo, y por
 *     eso ese paso en particular no se puede alcanzar pegando la URL.
 */

export const ONBOARDING_STEPS = [
  {
    key: "owners",
    title: "Carga la información de los propietarios",
    description:
      "Es el primer paso: todo lo demás (alícuotas, saldos, planillas) se apoya en los propietarios. Los inmuebles ya existen sin datos. Si son muchos, descarga la plantilla de Excel, complétala y súbela. Si son pocos, complétalos uno a uno con la opción “Editar” del menú (⋯) de cada inmueble.",
    cta: "Ir a Propietarios",
    to: () => ({
      pathname: "/dashboard/properties",
      search: "?onboarding=owners",
    }),
  },
  {
    key: "aliquot",
    title: "Carga la alícuota",
    description:
      "Si tu condominio reparte los gastos en partes iguales no tienes que hacer nada: el sistema ya lo hace así por defecto. Si cada inmueble paga un porcentaje distinto, descarga el Excel de alícuotas y cárgalas.",
    cta: "Ir a Alícuotas",
    to: () => ({
      pathname: "/dashboard/aliquots",
      search: "?onboarding=aliquot",
    }),
  },
  {
    key: "balances",
    title: "Carga los saldos iniciales",
    description:
      "Es lo que cada propietario ya debía antes de empezar a usar Homelan. Con la plantilla de efectos por cobrar puedes cargar el acumulado de cada uno o el detalle mes por mes; también puedes registrarlos uno a uno con el botón “Registrar Saldo” de cada inmueble.",
    cta: "Ir a Propietarios",
    to: () => ({
      pathname: "/dashboard/properties",
      search: "?onboarding=balances",
    }),
    // Un condominio que arranca de cero puede no tener nada que cargar acá.
    dismissLabel: "No tengo saldos iniciales",
  },
  {
    key: "expenses",
    title: "Registra los gastos ordinarios",
    description:
      "Son los gastos que se repiten todos los meses y se reparten entre los propietarios según su alícuota.",
    cta: "Ir a Gastos ordinarios",
    to: (condominiumId) => ({
      pathname: `/dashboard/finance/${condominiumId}/common-costs`,
      search: "?onboarding=expenses",
      state: { type_id: 1, type_name: "Gastos ordinarios" },
    }),
  },
  {
    key: "periods",
    title: "Revisa los períodos",
    description:
      "La planilla se emite contra un período. Al crear el condominio ya se generaron dos: el mes anterior (donde van los saldos iniciales) y el mes actual. Verifica que las fechas sean las que esperas antes de liquidar.",
    cta: "Ir a Períodos",
    to: () => ({
      pathname: "/dashboard/periods",
      search: "?onboarding=periods",
    }),
  },
  {
    key: "preview",
    title: "Previsualiza la planilla de liquidación de gastos",
    description:
      "Antes de registrar los gastos y emitir la planilla, verifica que tengas cargados todos los gastos ordinarios, extraordinarios y particulares, los ingresos y los fondos de reserva. La previsualización te muestra exactamente qué se le va a cobrar a cada propietario.",
    cta: "Previsualizar la planilla",
    to: (condominiumId) => ({
      pathname: `/dashboard/finance/${condominiumId}/form`,
      search: "?onboarding=preview",
    }),
    dismissLabel: "Ya la revisé",
  },
];

/**
 * Paso de bienvenida: vive solo en el frontend. No va en ONBOARDING_STEPS
 * porque no es una tarea: no tiene clave en el backend (tiene que coincidir
 * con OnboardingController::STEPS), no se marca como completado y no cuenta
 * en el progreso. OnboardingChecklist lo pinta fijo como primer paso,
 * abierto por defecto, con la invitacion a agendar una llamada de apoyo
 * para la carga inicial y, al lado, el acceso directo a la misma carga
 * inicial (todo en un archivo) que esta en Propietarios.
 */
export const ONBOARDING_WELCOME_STEP = {
  key: "welcome",
  title: "¡Bienvenido a Homelan!",
  description:
    "Sabemos que arrancar es lo que más cuesta. Si quieres, agenda una llamada con nosotros y te apoyamos con la carga inicial de tu condominio: propietarios, alícuotas y saldos. Son 30 minutos, en el día y la hora que mejor te convenga. Si prefieres hacerlo tú mismo, usa la carga inicial: descargas un solo archivo, lo completas y lo subes.",
  cta: "Agendar una llamada",
  url: "https://calendly.com/homelan/30min",
  initialLoadCta: "Carga inicial (todo en un archivo)",
};

/** Índice de pasos por clave, para el banner de cada módulo. */
export const ONBOARDING_STEP_BY_KEY = ONBOARDING_STEPS.reduce(
  (acc, step, index) => ({ ...acc, [step.key]: { ...step, position: index + 1 } }),
  {}
);

export const ONBOARDING_TOTAL_STEPS = ONBOARDING_STEPS.length;
